/* =========================================================
   ASIAN BASKET STOREFRONT TAXONOMY

   Navigation rule:
   Grocery: Department -> Category
   Takeout: Department -> Category

   NO third-level subcategory/chip navigation.
   ========================================================= */

/* =========================================================
   GROCERY FILTERS
   ========================================================= */

const GROCERY_FILTERS = [
  {
    key: "brand",
    label: "Brand",
    type: "dynamic",
  },

  {
    key: "price",
    label: "Price",
    type: "price",
  },

  {
    key: "sold_by",
    label: "Sold By",
    type: "options",
    options: ["Each", "Bunch", "Bag", "Pack", "lb", "kg"],
  },

  {
    key: "dietary",
    label: "Dietary / Product Attributes",
    type: "options",
    options: ["Vegan", "Gluten-Free", "Organic", "Halal"],
  },

  {
    key: "availability",
    label: "Availability",
    type: "availability",
    options: ["In Stock", "Out of Stock"],
  },

  {
    key: "origin",
    label: "Country of Origin",
    type: "dynamic",
  },
];

/* =========================================================
   TAKEOUT FILTERS
   ========================================================= */

const TAKEOUT_FILTERS = [
  {
    key: "price",
    label: "Price",
    type: "price",
  },

  {
    key: "dietary",
    label: "Dietary / Preferences",
    type: "options",
    options: ["Vegan", "Jain", "Gluten-Free", "No Onion", "No Garlic"],
  },

  {
    key: "serving_size",
    label: "Serving Size",
    type: "options",
    options: ["Individual", "Shareable", "Family"],
  },

  {
    key: "availability",
    label: "Availability",
    type: "availability",
    options: ["Available", "Sold Out"],
  },
];

/* =========================================================
   GROCERY DEPARTMENTS
   ========================================================= */

export const ASIAN_BASKET_GROCERY_DEPARTMENTS = {
  "fresh-food": {
    label: "Fresh Food",

    icon: "/assets/icons/grocery-bottom-1.svg",

    storefront: "grocery",

    categories: {
      "Fresh Vegetables": [],
      "Fresh Fruits": [],
      "Herbs, Chillies & Aromatics": [],
      "Dairy & Paneer": [],
      "Bakery & Breads": [],
      "Refrigerated Foods": [],
    },

    filters: GROCERY_FILTERS,
  },

  groceries: {
    label: "Groceries",

    icon: "/assets/icons/grocery-bottom-2.svg",

    storefront: "grocery",

    categories: {
      "Atta & Flour": [],
      Rice: [],
      "Dal & Pulses": [],
      "Spices & Masalas": [],
      "Oils & Ghee": [],
      "Salt, Sugar & Jaggery": [],
      "Pickles & Chutneys": [],
      "Sauces & Condiments": [],
      "Canned & Jarred Foods": [],
      "Noodles, Pasta & Vermicelli": [],
      "Breakfast & Cereals": [],
      "Instant Mixes & Ready-to-Cook": [],
      "Tea, Coffee & Packaged Beverages": [],
      "Baking & Dessert Ingredients": [],
    },

    filters: GROCERY_FILTERS,
  },

  "ready-food": {
    label: "Ready Food",

    icon: "/assets/icons/grocery-bottom-3.svg",

    storefront: "grocery",

    categories: {
      "Ready-to-Eat Meals": [],
      "Frozen Meals": [],
      "Frozen Breads": [],
      "Frozen Snacks & Appetizers": [],
      "Ready-to-Heat Snacks": [],
      "Refrigerated Prepared Foods": [],
    },

    filters: GROCERY_FILTERS,
  },

  "snacks-sweets": {
    label: "Snacks & Sweets",

    icon: "/assets/icons/grocery-bottom-4.svg",

    storefront: "grocery",

    categories: {
      "Namkeen & Savoury Snacks": [],
      "Chips & Crackers": [],
      "Biscuits & Cookies": [],
      "Packaged Mithai & Sweets": [],
      "Chocolates & Candy": [],
      "Nuts & Dried Fruit": [],
    },

    filters: GROCERY_FILTERS,
  },

  "personal-household": {
    label: "Personal & Household",

    icon: "/assets/icons/grocery-bottom-5.svg",

    storefront: "grocery",

    categories: {
      "Personal Care": [],
      "Hair Care": [],
      "Skin Care": [],
      "Oral Care": [],
      "Baby Care": [],
      "Household Cleaning": [],
      Laundry: [],
      "Kitchen & Disposable Supplies": [],
    },

    filters: GROCERY_FILTERS,
  },

  pooja: {
    label: "Pooja",

    icon: "/assets/icons/grocery-bottom-6.svg",

    storefront: "grocery",

    categories: {
      "Incense & Dhoop": [],
      "Diyas & Candles": [],
      "Pooja Oils & Ghee": [],
      "Pooja Kits & Supplies": [],
      "Religious Items": [],
      "Garlands & Decorative Pooja Items": [],
    },

    filters: GROCERY_FILTERS,
  },
};

/* =========================================================
   TAKEOUT DEPARTMENTS
   ========================================================= */

export const ASIAN_BASKET_TAKEOUT_DEPARTMENTS = {
  "desi-meals": {
    label: "Desi Meals",

    icon: "/assets/icons/takeout-bottom-1.png",

    storefront: "takeout",

    categories: {
      Curries: [],
      Thalis: [],
      "Rice & Biryani": [],
      "Dosa & South Indian": [],
      "Noodles & Indo-Chinese": [],
      "Breads & Sides": [],
      "Combos & Family Meals": [],
    },

    filters: TAKEOUT_FILTERS,
  },

  "chaat-bazaar": {
    label: "Chaat Bazaar",

    icon: "/assets/icons/takeout-bottom-2.png",

    storefront: "takeout",

    categories: {
      "Pani Puri / Golgappa": [],
      "Papdi Chaat": [],
      "Aloo Tikki": [],
      "Pav Bhaji": [],
      "Chole Bhature": [],
      "Dahi Chaat": [],
      "Other Street Food": [],
    },

    filters: TAKEOUT_FILTERS,
  },

  "nashta-savouries": {
    label: "Nashta & Savouries",

    icon: "/assets/icons/takeout-bottom-3.png",

    storefront: "takeout",

    categories: {
      Samosa: [],
      "Pakora / Bhajia": [],
      Patties: [],
      Kachori: [],
      "Wraps & Rolls": [],
      Sandwiches: [],
      "Other Savouries": [],
    },

    filters: TAKEOUT_FILTERS,
  },

  "mithai-cakes": {
    label: "Mithai & Cakes",

    icon: "/assets/icons/takeout-bottom-4.png",

    storefront: "takeout",

    categories: {
      "Fresh Mithai": [],
      "Whole Cakes": [],
      "Cake Slices": [],
      Pastries: [],
      "Indian Desserts": [],
      "Celebration Cakes": [],
    },

    filters: TAKEOUT_FILTERS,
  },

  "lassi-fresh-drinks": {
    label: "Lassi & Fresh Drinks",

    icon: "/assets/icons/takeout-bottom-5.png",

    storefront: "takeout",

    categories: {
      Lassi: [],
      Falooda: [],
      "Fresh Juice": [],
      Shakes: [],
      "Chai & Hot Drinks": [],
      "Specialty Drinks": [],
    },

    filters: TAKEOUT_FILTERS,
  },
};

/* =========================================================
   NAVIGATION ORDER
   ========================================================= */

export const ASIAN_BASKET_GROCERY_DEPARTMENT_ORDER = [
  "fresh-food",
  "groceries",
  "ready-food",
  "snacks-sweets",
  "personal-household",
  "pooja",
];

export const ASIAN_BASKET_TAKEOUT_DEPARTMENT_ORDER = [
  "desi-meals",
  "chaat-bazaar",
  "nashta-savouries",
  "mithai-cakes",
  "lassi-fresh-drinks",
];

/* =========================================================
   BACKWARD COMPATIBILITY

   Keep these exports because other existing components
   may already import ASIAN_BASKET_DEPARTMENTS.
   ========================================================= */

export const ASIAN_BASKET_DEPARTMENTS = {
  ...ASIAN_BASKET_GROCERY_DEPARTMENTS,

  "take-out": {
    label: "Take Out",

    icon: "/assets/icons/bowl.svg",

    storefront: "takeout",

    defaultDepartment: "desi-meals",

    categories: ASIAN_BASKET_TAKEOUT_DEPARTMENTS["desi-meals"].categories,

    filters: TAKEOUT_FILTERS,
  },
};

export const ASIAN_BASKET_DEPARTMENT_ORDER =
  ASIAN_BASKET_GROCERY_DEPARTMENT_ORDER;
